// Copyright 2021-2024, 2026 - Hewlett Packard Enterprise Company

import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useFlags from '../hooks/useFlags';
import { AuthConsumer } from '../providers/AuthProvider';
import { isDevEnvAuth, postLoginRedirect } from '../utils/authHelpers';
import {
  WORKSPACE_ID_KEY,
  CUSTOMER_ID_KEY,
  DEV_ACID_KEY,
  TENANT_ACID_KEY,
  REDIRECT_URI_KEY,
  REDIRECT_SEARCH_PARAMS_KEY,
} from '../constants/authConst';

const CCS_LOGIN_URL = window.indigo.REACT_APP_CCS_LOGIN;
const APP_INSTANCE_ID = window.indigo.REACT_APP_CCS_APP_INSTANCE_ID;

const GLCP_QUERY_PARAMS = [
  'cid',
  'workspace_id',
  'account_type',
  'service_offer',
];

export default function Login() {
  const history = useHistory();
  const location = useLocation();
  const flags = useFlags();
  const preselectWorkspaceFlag =
    flags.T_20260219_122476_INDIGO_PRESELECT_SAME_WORKSPACE_BOOL();

  return (
    <AuthConsumer>
      {({ getUser, login, getCid, updateTenantAcid }) => {
        async function doLogin() {
          const params = new URLSearchParams(location.search);

          const loginParams = new Map();
          // parse out GLCP specific query params from login flow
          GLCP_QUERY_PARAMS.forEach(param => {
            if (params.has(param)) {
              loginParams.set(param, params.get(param));
              params.delete(param);
            }
          });

          // if the user was redirected to login from a different route, preserve the route info
          if (window.location.pathname !== '/login') {
            sessionStorage.setItem(REDIRECT_URI_KEY, window.location.pathname);
            sessionStorage.setItem(
              REDIRECT_SEARCH_PARAMS_KEY,
              params.toString(),
            );
          }

          let cid = loginParams.get('cid');
          let workspaceId = loginParams.get('workspace_id');
          // always start navigation on MSP page
          updateTenantAcid(null);

          const user = await getUser();
          if (user) {
            // use case: user navigated back to GLCP and then launched COM again
            if (cid && cid !== (await getCid())) {
              // use case: user switched workspaces while in GLCP
              localStorage.clear(TENANT_ACID_KEY);
              localStorage.clear(DEV_ACID_KEY);
              await login({ cid, workspaceId });
            } else {
              // user is already authenticated, skip login flow
              postLoginRedirect(history);
            }
            return;
          }

          // The cid parameter is a GLP concept. We fake one for
          // local development when we are not pointing at a GLP auth server
          if (!cid && isDevEnvAuth()) {
            cid = 'aci1';
          }

          if (preselectWorkspaceFlag && !cid && !workspaceId) {
            // use case: user manually navigated to COM or opened a COM link in a new tab/window
            // attempt to load cid and workspaceId from localStorage and provide to the GLCP login
            // flow, potentially bypassing GLCP prompt for the workspace
            cid = localStorage.getItem(CUSTOMER_ID_KEY) || undefined;
            workspaceId = localStorage.getItem(WORKSPACE_ID_KEY) || undefined;
          }

          if (cid) {
            // begin oidc flow to get access token
            await login({ cid, workspaceId });
          } else {
            // use case: customer directly navigated to COM without going through GLCP and no
            // existing session via query params or storage.
            // redirect to GLP /login?app=<application_instance_id> to login and select workspace
            window.location.replace(`${CCS_LOGIN_URL}?app=${APP_INSTANCE_ID}`);
          }
        }

        doLogin();
      }}
    </AuthConsumer>
  );
}
